/*
 * Copyright (c) 2021. FINN Sp. z o.o.
 */

package pl.finn.forms.utils

import io.kvision.core.*
import io.kvision.form.check.CheckBox
import io.kvision.form.check.RadioGroup
import io.kvision.form.select.Select
import io.kvision.form.select.selectOption
import io.kvision.form.text.Text
import io.kvision.form.text.TextArea
import io.kvision.form.time.DateTime
import io.kvision.html.Div
import io.kvision.panel.FieldsetPanel
import io.kvision.panel.SimplePanel
import io.kvision.types.toDateF
import pl.finn.forms.POLE_WYMAGANE
import pl.finn.forms.tw1.DocumentTreeNode
import pl.finn.forms.utils.form.FormBuilder
import pl.finn.forms.utils.form.FormElement

import io.kvision.form.text.Imask
import io.kvision.form.text.ImaskOptions
import io.kvision.form.text.*

class Components(reference: FormBuilder<DocumentTreeNode>) {

  private val formReference = reference

  fun createSelect(
    fieldValue: DocumentTreeNode,
    label: String,
    selectOptions: List<String>,
    isLiveSearch: Boolean = false,
    tooltipMessage: String? = null,
    isRequired: Boolean = true
  ): FormElement {
    val formElement = FormElement(
      fieldValue,
      Select(label = label).apply {
        for ((i, enum) in selectOptions.withIndex()) {
          selectOption(i.toString(), enum)
          liveSearch = isLiveSearch
        }
        enableTooltip(TooltipOptions(tooltipMessage, rich = true, placement = Placement.BOTTOM))
      },
      required = isRequired,
      requiredMessage = POLE_WYMAGANE
    )
    formReference.getElement(formElement)
    return formElement
  }

  fun createSelectForKraj(
    fieldValue: DocumentTreeNode,
    label: String,
    selectOptions: List<String>,
    isLiveSearch: Boolean = false,
    tooltipMessage: String? = null,
    isRequired: Boolean = true
  ): FormElement {
    val formElement = FormElement(
      fieldValue,
      Select(label = label).apply {
        for ((i, enum) in selectOptions.withIndex()) {
          selectOption(enum, enum)
          liveSearch = isLiveSearch
        }
        enableTooltip(TooltipOptions(tooltipMessage, rich = true, placement = Placement.BOTTOM))
      },
      required = isRequired,
      requiredMessage = POLE_WYMAGANE
    )
    formReference.getElement(formElement)
    return formElement
  }

  fun createInput(
    fieldValue: DocumentTreeNode,
    label: String,
    tooltipMessage: String? = null,
    isRequired: Boolean = true,
    requirements: Array<String?> = arrayOf(null, null)
  ): FormElement {
    val formElement = FormElement(
      fieldValue,
      Text(label = label, rich = true).apply {
        enableTooltip(TooltipOptions(tooltipMessage, rich = true, placement = Placement.BOTTOM))
      },
      required = isRequired,
      requiredMessage = POLE_WYMAGANE,
      validatorMessage = { requirements[1] }) {
      if (requirements[0] != null) it.getValueAsString()?.let { it1 -> (requirements[0])?.toRegex()?.matches(it1) }
      else true
    }
    formReference.getElement(formElement)
    return formElement
  }

  fun createTextArea(
    fieldValue: DocumentTreeNode,
    label: String,
    rows: Int = 3,
    tooltipMessage: String? = null,
    isRequired: Boolean = true
  ): FormElement {
    val formElement = FormElement(
      fieldValue,
      TextArea(label = label, rows = rows, rich = true).apply {
        enableTooltip(TooltipOptions(tooltipMessage, rich = true, placement = Placement.BOTTOM))
      },
      required = isRequired,
      requiredMessage = POLE_WYMAGANE
    )
    formReference.getElement(formElement)
    return formElement
  }

  fun createDateTime(
    fieldValue: DocumentTreeNode,
    label: String,
    tooltipMessage: String? = null,
    isRequired: Boolean = true
  ): FormElement {
    val date = fieldValue.value?.toDateF("YYYY-MM-DD")
    val formElement = FormElement(
      fieldValue,
      DateTime(label = label, format = "YYYY-MM-DD") {
        enableTooltip(TooltipOptions(tooltipMessage, rich = true, placement = Placement.BOTTOM))
      },
      required = isRequired,
      requiredMessage = POLE_WYMAGANE,
      validatorMessage = { "Niewłaściwy format daty" }) {
      it.getValueAsString()?.let { it1 -> ("((\\d{4})-(\\d{2})-(\\d{2}))")?.toRegex()?.matches(it1) }
    }

    formReference.getElement(formElement)
    if (date != null) formElement.field.setValue(date)
    return formElement
  }

  fun createCollapseCheckbox(
    fieldValue: DocumentTreeNode,
    label: String,
    elementId: String,
    nestedElements: ArrayList<Any>,
    collapsedDiv: Div
  ): FormElement {
    val isChecked = fieldValue.value.equals("true")
    val collapseStatus = if (isChecked) "show" else ""
    val formElement = FormElement(
      fieldValue,
      CheckBox(label = label, rich = true) {
        addCssClass("mt-3")
        addCssClass("w-fit")
        setAttribute("data-bs-toggle", "collapse")
        setAttribute("data-bs-target", "#$elementId")
        setAttribute("aria-expanded", isChecked.toString())
        setAttribute("aria-controls", elementId)
        onClick {
          formReference.getData()
          val status = this.value
          changeElementChildrenStatus(nestedElements, status)
          if (status) collapsedDiv.addCssClass("show") else collapsedDiv.removeCssClass("show")
        }
      }
    )
    collapsedDiv.addCssClass(collapseStatus)
    formReference.getElement(formElement)
    return formElement
  }

  fun createRadioGroup(
    fieldValue: DocumentTreeNode,
    label: String,
    optionList: List<StringPair>,
    mapHiddenElement: HashMap<String, Any?>? = null,
    isRequired: Boolean = true
  ): FormElement {
    var annotationLength = 0

    optionList.forEach { annotationLength += it.second.length }

    val formElement = FormElement(
      fieldValue,
      RadioGroup(options = optionList, label = label, inline = annotationLength < 50, rich = true).apply {
        onEvent {
          change = {
            changeRadioVisibility(mapHiddenElement, this.self.value)
          }
        }
      },
      required = isRequired,
      requiredMessage = POLE_WYMAGANE,
    )
    if (fieldValue.value != null) changeRadioVisibility(mapHiddenElement, fieldValue.value)
    formReference.getElement(formElement)
    return formElement
  }

  fun createCheckbox(
    fieldValue: DocumentTreeNode,
    label: String,
    value: Boolean = false,
    isRequired: Boolean = true
  ): FormElement {
    val formElement = FormElement(
      fieldValue,
      CheckBox(label = label, rich = true),
      required = isRequired,
      requiredMessage = POLE_WYMAGANE,
    )
    formReference.getElement(formElement)
    return formElement
  }

  fun createFieldset(
    label: String,
  ): FormComponent {
    return FormComponent(
      FieldsetPanel(label)
    )
  }

  var radioGroup: FormElement? = null
  fun changeElementChildrenStatus(nestedElements: Any?, status: Boolean) {
    when (nestedElements) {
      is ArrayList<*> -> nestedElements.forEach { changeElementChildrenStatus(it, status) }
      is FormElement -> {
        if (nestedElements.field is RadioGroup) radioGroup = nestedElements
        nestedElements.field.visible = status
        if (status) (nestedElements.field as SimplePanel).show()
        else (nestedElements.field as SimplePanel).hide()
      }
      is FormComponent -> {
        if (status) (nestedElements.field as Component).removeCssClass("d-none")
        else (nestedElements.field as Component).addCssClass("d-none")
      }
      is Div -> {
        if (status) (nestedElements as Component).removeCssClass("d-none")
        else (nestedElements as Component).addCssClass("d-none")
      }
      is HashMap<*, *> ->
        if (status) changeRadioVisibility(
          nestedElements as HashMap<String, Any?>?,
          (radioGroup?.field as? RadioGroup)?.value
        )
        else nestedElements.forEach { changeElementChildrenStatus(it.value, status) }
    }
  }

  fun readComponentsList(nestedElements: Any?, div: Container, formElement: FormElement? = null) {
    var panel: Container = div
    var radioGroup: FormElement? = formElement
    if (nestedElements is ArrayList<*>) {
      nestedElements.forEach {
        when (it) {
          is ArrayList<*> -> {
            readComponentsList(it, panel)
            panel = div
          }
          is HashMap<*, *> -> {
            it.forEach {
              readComponentsList(it.value, div)
            }
            changeRadioVisibility(it as HashMap<String, Any?>?, (radioGroup?.field as? RadioGroup)?.value)
          }
          is FormComponent -> {
            panel.add(it.field); panel = it.field
          }
          is Div -> {
            panel.add(it); panel = it
          }
          is FormElement -> {
            if (it.field is RadioGroup) radioGroup = it
            div.add(it.field)
          }
        }
      }
    } else {
      when (nestedElements) {
        is HashMap<*, *> -> {
          nestedElements.forEach {
            readComponentsList(it.value, div)
          }
          changeRadioVisibility(nestedElements as HashMap<String, Any?>?, (radioGroup?.field as? RadioGroup)?.value)
        }
        is FormComponent -> {
          panel.add(nestedElements.field); panel = nestedElements.field
        }
        is Div -> {
          panel.add(nestedElements); panel = nestedElements
        }
        is FormElement -> div.add(nestedElements.field)
      }
    }
  }

  fun changeRadioVisibility(
    maphiddenElement: HashMap<String, Any?>?, choosen: String?
  ) {
    maphiddenElement?.forEach { (key, values) ->
      if (choosen == key) {
        changeElementChildrenStatus(values, true)
      } else {
        changeElementChildrenStatus(values, false)
      }
    }
  }
}
